import './bootstrap';
import '../css/app.css';

import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';

// Monaco Editor: CDN URL を一度だけ設定する
import { loader } from '@guolao/vue-monaco-editor';
loader.config({
    paths: {
        vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.55.1/min/vs',
    },
    'vs/nls': { availableLanguages: {} },
});
// 複数の VueMonacoEditor が同じ言語で同時にマウントされると、Monaco の AMD ローダーが
// 同一モジュール（例: vs/python-XXXXXXXX）を競合して define しようとして console.warn を出す。
// 機能上は問題ないため、この特定の警告のみを抑制する。
const _warn = console.warn.bind(console);
console.warn = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].startsWith("Duplicate definition of module 'vs/")) {
        return;
    }
    _warn(...args);
};

import PrimeVue from 'primevue/config';
import BadgeDirective from 'primevue/badgedirective';
import ConfirmationService from 'primevue/confirmationservice';
import DialogService from 'primevue/dialogservice';
import Ripple from 'primevue/ripple';
import StyleClass from 'primevue/styleclass';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

import 'primevue/resources/themes/tailwind-light/theme.css';
// import '@/assets/styles.scss';
import 'primeicons/primeicons.css';

import { config } from 'md-editor-v3';
import JP_JP from '@vavt/cm-extension/dist/locale/jp-JP';

const appName = import.meta.env.VITE_APP_NAME || 'OLS v2';

config({
    editorConfig: {
        languageUserDefined: {
            ja_JP: JP_JP
        }
    },
    // mermaid のラベルを HTML(foreignObject) ではなく SVG テキストで描画する。
    // HTML ラベルは既定フォントで幅を計測した固定幅コンテナに入るため、
    // 日本語（実描画フォントの方が広い）でラベル末尾が切れる。
    // SVG テキストは実 DOM 上の実測(getBBox)で箱がラベル幅に追随するため切れない。
    // ※実行時の mermaid は md-editor-v3 が CDN から読み込む v11 系（package.json には無い）
    mermaidConfig: (base: Record<string, unknown>) => ({
        ...base,
        htmlLabels: false,
        // 図全体の文字を大きくする（既定 16px。計測も同じ値で行われ箱も追随する）
        themeVariables: { fontSize: '18px' },
        flowchart: {
            htmlLabels: false,
            // ノードラベルは既定 200px で強制折り返しされ日本語が細切れになるため広げる
            // （明示的な改行は <br> で指定する運用）
            wrappingWidth: 600,
            // 余白を詰めて図の密度を上げる（既定は各 50）
            nodeSpacing: 22,
            rankSpacing: 24,
            // subgraph タイトルの上下余白（既定 0）。0 だとタイトルが先頭ノードの箱に
            // 重なるため、囲み枠を上下に広げてタイトル周りのスペースを確保する
            subGraphTitleMargin: { top: 10, bottom: 20 }
        }
    }),
    // Markdown プレビューで連続空白（2 個以上）を縮退させずに表示する。
    // HTML の既定（white-space: normal）では連続空白が 1 個に潰れるため、
    // 出力 HTML の段階で 2 個目以降の空白を &nbsp; に変換する。
    // text トークンのみを対象にしているため、code_inline / fence / KaTeX 等は影響を受けない。
    markdownItConfig: (md) => {
        const escapeHtml = md.utils.escapeHtml;
        md.renderer.rules.text = (tokens, idx) => {
            const content = tokens[idx]?.content ?? '';
            const escaped = escapeHtml(content);
            return escaped.replace(/ {2,}/g, (match) => ' ' + '&nbsp;'.repeat(match.length - 1));
        };

        // 埋め込み Markdown 画像 `![](images/foo.png?width=NN)` の表示サイズ指定に対応する。
        // 添付画像(imageWidthPercentage)と挙動を揃える目的で、src の `?width=NN`
        // （＝ページ幅比の整数パーセント）を解釈して img に width:NN% を付与する。
        //   - NN は 1〜100 にクランプ。指定が無ければ従来どおり自然サイズ（max-width:100%）。
        //   - 幅指定図は「指定幅のブロック（左寄せ）」として表示（height は自動でアスペクト保持）。
        //   - 受験者(MdPreview)・編集(MdEditor)の両プレビューに一括適用（本設定はグローバル）。
        //   - medium-zoom の `img.medium-zoom-image{width:auto !important}` 上書きに勝たせるため、
        //     width のみインライン `!important` を付ける（クリック拡大は従来どおり機能）。
        //   - width クエリは src から除去して URL をクリーンに保つ（サーバは元々 ?width を無視）。
        type ImageRule = NonNullable<typeof md.renderer.rules.image>;
        const defaultImageRenderer: ImageRule =
            md.renderer.rules.image ??
            ((tokens, idx, options, _env, self) => self.renderToken(tokens, idx, options));
        md.renderer.rules.image = (tokens, idx, options, env, self) => {
            const token = tokens[idx];
            let src = token?.attrGet('src') ?? null;
            // 取り込みコンテンツ等に残る相対参照 `images/…` を公開ストレージ URL へ解決する
            // （バックエンド MarkdownImagePath::resolveToStorage と同じ規約の表示側フォールバック。
            //   `/storage/…` や `http(s)://…` の絶対参照は触らない）。
            if (token && src && /^(?:\.\/)?images\//.test(src)) {
                src = '/storage/images/' + src.replace(/^(?:\.\/)?images\//, '');
                token.attrSet('src', src);
            }
            const matched = src ? src.match(/[?&]width=(\d{1,3})%?/i) : null;
            if (token && src && matched) {
                const pct = Math.min(100, Math.max(1, parseInt(matched[1] ?? '', 10) || 1));
                // width クエリを src から除去し、余った ? / & を整える
                const cleaned = src
                    .replace(/([?&])width=\d{1,3}%?/i, '$1')
                    .replace(/\?&/, '?')
                    .replace(/[?&]$/, '');
                token.attrSet('src', cleaned);
                const prevStyle = (token.attrGet('style') ?? '').replace(/;?\s*$/, '');
                token.attrSet(
                    'style',
                    `${prevStyle ? prevStyle + ';' : ''}display:block;width:${pct}% !important;height:auto`
                );
                const prevClass = token.attrGet('class');
                token.attrSet('class', prevClass ? `${prevClass} ols-md-sized-img` : 'ols-md-sized-img');
            }
            return defaultImageRenderer(tokens, idx, options, env, self);
        };
    }
});

void createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob<DefineComponent>('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue);

        app.use(PrimeVue, { ripple: true });
        app.use(ToastService);
        app.use(DialogService);
        app.use(ConfirmationService);

        app.directive('tooltip', Tooltip);
        app.directive('badge', BadgeDirective);
        app.directive('ripple', Ripple);
        app.directive('styleclass', StyleClass);

        app.mount(el);
    },
    progress: {
        color: '#ff4646'
    }
});
